/**
 * Static validator for the JSON-LD @graph produced by buildSiteGraph().
 *
 * Surfaces:
 *  - missing / empty required fields (errors)
 *  - malformed values (errors): bad URLs, non-numeric geo, bad telephone,
 *    bad postcode, bad opening time format
 *  - recommended-but-missing fields (warnings) per Google's guidelines
 *
 * Intentionally dependency-free; consumed by the admin settings panel
 * to highlight problems before "Save & regenerate" is clicked.
 */

export type IssueSeverity = "error" | "warning";

export interface SchemaIssue {
  /** Dotted path inside the offending node, e.g. "address.streetAddress". */
  path: string;
  /** Schema node where the issue lives, e.g. "LocalBusiness" or "Service: Upper Blepharoplasty". */
  node: string;
  /** Human-readable explanation. */
  message: string;
  severity: IssueSeverity;
  /**
   * Form field (in ClinicSettings) the user should fix, when known.
   * Used to anchor the badge tooltip / "Jump to field" UX.
   */
  formField?: string;
}

export interface SchemaValidationResult {
  issues: SchemaIssue[];
  errorCount: number;
  warningCount: number;
  hasError: boolean;
}

type Node = Record<string, unknown>;

const URL_RE = /^https?:\/\/[^\s]+$/i;
const TEL_RE = /^\+[\d\s().-]{7,24}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const UK_POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

const isNonEmptyString = (v: unknown): v is string =>
  typeof v === "string" && v.trim().length > 0;

const isFiniteNumber = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);

const nodeTypeLabel = (n: Node): string => {
  const t = n["@type"];
  if (Array.isArray(t)) return (t as string[])[0] ?? "Node";
  return typeof t === "string" ? t : "Node";
};

const hasType = (n: Node, type: string): boolean => {
  const t = n["@type"];
  if (Array.isArray(t)) return (t as string[]).includes(type);
  return t === type;
};

// ---------- per-node validators ----------

const validateClinicNode = (n: Node, push: (i: SchemaIssue) => void) => {
  const node = "LocalBusiness / MedicalClinic";

  if (!isNonEmptyString(n.name)) {
    push({ path: "name", node, message: "Name is required.", severity: "error", formField: "name" });
  }
  if (!isNonEmptyString(n.telephone)) {
    push({ path: "telephone", node, message: "Telephone is required.", severity: "error", formField: "phone_international" });
  } else if (!TEL_RE.test(n.telephone as string)) {
    push({ path: "telephone", node, message: "Telephone must be E.164, e.g. +442033703503.", severity: "error", formField: "phone_international" });
  }
  if (!isNonEmptyString(n.url) || !URL_RE.test(n.url as string)) {
    push({ path: "url", node, message: "Site URL must be a valid http(s) URL.", severity: "error" });
  }
  if (!isNonEmptyString(n.image) || !URL_RE.test(n.image as string)) {
    push({ path: "image", node, message: "Image URL is missing or malformed.", severity: "warning" });
  }

  // Address
  const addr = n.address as Node | undefined;
  if (!addr || typeof addr !== "object") {
    push({ path: "address", node, message: "PostalAddress is required.", severity: "error", formField: "street" });
  } else {
    const checks: Array<[string, string, string]> = [
      ["streetAddress", "Street address is required.", "street"],
      ["addressLocality", "Locality / district is required.", "locality"],
      ["addressRegion", "Region / city is required.", "region"],
      ["postalCode", "Postcode is required.", "postal_code"],
      ["addressCountry", "Country code is required.", "country_code"],
    ];
    for (const [k, msg, field] of checks) {
      if (!isNonEmptyString(addr[k])) {
        push({ path: `address.${k}`, node, message: msg, severity: "error", formField: field });
      }
    }
    if (isNonEmptyString(addr.addressCountry) && (addr.addressCountry as string).length !== 2) {
      push({ path: "address.addressCountry", node, message: "Country must be a 2-letter ISO code (e.g. GB).", severity: "error", formField: "country_code" });
    }
    if (
      isNonEmptyString(addr.addressCountry) &&
      (addr.addressCountry as string).toUpperCase() === "GB" &&
      isNonEmptyString(addr.postalCode) &&
      !UK_POSTCODE_RE.test(addr.postalCode as string)
    ) {
      push({ path: "address.postalCode", node, message: "UK postcode looks malformed (expected e.g. SW1A 1AA).", severity: "warning", formField: "postal_code" });
    }
  }

  // Geo
  const geo = n.geo as Node | undefined;
  if (!geo || typeof geo !== "object") {
    push({ path: "geo", node, message: "GeoCoordinates missing — recommended for local SEO.", severity: "warning", formField: "latitude" });
  } else {
    if (!isFiniteNumber(geo.latitude)) {
      push({ path: "geo.latitude", node, message: "Latitude must be a number.", severity: "error", formField: "latitude" });
    } else if ((geo.latitude as number) < -90 || (geo.latitude as number) > 90) {
      push({ path: "geo.latitude", node, message: "Latitude out of range (−90…90).", severity: "error", formField: "latitude" });
    }
    if (!isFiniteNumber(geo.longitude)) {
      push({ path: "geo.longitude", node, message: "Longitude must be a number.", severity: "error", formField: "longitude" });
    } else if ((geo.longitude as number) < -180 || (geo.longitude as number) > 180) {
      push({ path: "geo.longitude", node, message: "Longitude out of range (−180…180).", severity: "error", formField: "longitude" });
    }
  }

  // Opening hours
  const hours = n.openingHoursSpecification;
  if (!Array.isArray(hours) || hours.length === 0) {
    push({ path: "openingHoursSpecification", node, message: "At least one opening-hours specification is recommended.", severity: "warning", formField: "hours" });
  } else {
    (hours as Node[]).forEach((h, i) => {
      const days = h.dayOfWeek;
      if (!Array.isArray(days) || days.length === 0) {
        push({ path: `openingHoursSpecification[${i}].dayOfWeek`, node, message: "Day list missing — fill the “Days (schema)” column.", severity: "error", formField: "hours" });
      }
      if (!isNonEmptyString(h.opens) || !TIME_RE.test(h.opens as string)) {
        push({ path: `openingHoursSpecification[${i}].opens`, node, message: "Opens time must be HH:MM (24-hour).", severity: "error", formField: "hours" });
      }
      if (!isNonEmptyString(h.closes) || !TIME_RE.test(h.closes as string)) {
        push({ path: `openingHoursSpecification[${i}].closes`, node, message: "Closes time must be HH:MM (24-hour).", severity: "error", formField: "hours" });
      }
    });
  }

  // sameAs URLs
  if (Array.isArray(n.sameAs)) {
    (n.sameAs as unknown[]).forEach((url, i) => {
      if (typeof url === "string" && url && !URL_RE.test(url)) {
        push({ path: `sameAs[${i}]`, node, message: "sameAs entry is not a valid URL.", severity: "error" });
      }
    });
  }

  // Contact points
  if (Array.isArray(n.contactPoint)) {
    (n.contactPoint as Node[]).forEach((cp, i) => {
      if (isNonEmptyString(cp.telephone) && !TEL_RE.test(cp.telephone as string)) {
        push({ path: `contactPoint[${i}].telephone`, node, message: "ContactPoint telephone must be E.164.", severity: "error", formField: cp.contactType === "customer support" ? "whatsapp_number" : "phone_international" });
      }
      if (isNonEmptyString(cp.url) && !URL_RE.test(cp.url as string)) {
        push({ path: `contactPoint[${i}].url`, node, message: "ContactPoint URL is malformed.", severity: "error", formField: "whatsapp_url" });
      }
    });
  }
};

const validatePhysicianNode = (n: Node, push: (i: SchemaIssue) => void) => {
  const node = "Physician";
  if (!isNonEmptyString(n.name)) {
    push({ path: "name", node, message: "Physician name is required.", severity: "error" });
  }
  if (!isNonEmptyString(n.url) || !URL_RE.test(n.url as string)) {
    push({ path: "url", node, message: "Physician URL is missing or malformed.", severity: "warning" });
  }
};

const validateServiceNode = (n: Node, push: (i: SchemaIssue) => void) => {
  const label = isNonEmptyString(n.name) ? (n.name as string) : "unnamed";
  const node = `Service: ${label}`;

  if (!isNonEmptyString(n.name)) {
    push({ path: "name", node, message: "Service name is required.", severity: "error" });
  }
  if (!isNonEmptyString(n.howPerformed)) {
    push({ path: "howPerformed", node, message: "howPerformed missing — recommended for MedicalProcedure.", severity: "warning" });
  }
  const offers = n.offers as Node | undefined;
  if (offers && typeof offers === "object") {
    if (!isNonEmptyString(offers.price) && !isFiniteNumber(offers.price)) {
      push({ path: "offers.price", node, message: "Offer price is required.", severity: "error" });
    }
    if (!isNonEmptyString(offers.priceCurrency)) {
      push({ path: "offers.priceCurrency", node, message: "Offer priceCurrency is required (e.g. GBP).", severity: "error" });
    }
    if (isNonEmptyString(offers.url) && !URL_RE.test(offers.url as string)) {
      push({ path: "offers.url", node, message: "Offer URL is malformed.", severity: "error" });
    }
  }
};

const validateFaqNode = (n: Node, push: (i: SchemaIssue) => void) => {
  const node = "FAQPage";
  const main = n.mainEntity;
  if (!Array.isArray(main) || main.length === 0) {
    push({ path: "mainEntity", node, message: "FAQ has no questions.", severity: "error" });
    return;
  }
  (main as Node[]).forEach((q, i) => {
    if (!isNonEmptyString(q.name)) {
      push({ path: `mainEntity[${i}].name`, node, message: `Question ${i + 1} is missing its name.`, severity: "error" });
    }
    const ans = q.acceptedAnswer as Node | undefined;
    if (!ans || typeof ans !== "object" || !isNonEmptyString(ans.text)) {
      push({ path: `mainEntity[${i}].acceptedAnswer.text`, node, message: `Question ${i + 1} is missing its answer.`, severity: "error" });
    }
  });
};

// ---------- top-level ----------

export const validateSiteGraph = (
  graph: { "@graph": Node[] } | Record<string, unknown>,
): SchemaValidationResult => {
  const issues: SchemaIssue[] = [];
  const push = (i: SchemaIssue) => issues.push(i);

  const nodes = (graph as { "@graph"?: Node[] })["@graph"];
  if (!Array.isArray(nodes) || nodes.length === 0) {
    push({ path: "@graph", node: "Graph", message: "Structured-data graph is empty.", severity: "error" });
  } else {
    let sawClinic = false;
    let sawPhysician = false;
    let sawService = false;
    let sawFaq = false;

    for (const n of nodes) {
      if (hasType(n, "LocalBusiness") || hasType(n, "MedicalClinic") || hasType(n, "MedicalOrganization")) {
        sawClinic = true;
        validateClinicNode(n, push);
      } else if (hasType(n, "Physician")) {
        sawPhysician = true;
        validatePhysicianNode(n, push);
      } else if (hasType(n, "MedicalProcedure") || hasType(n, "Service")) {
        sawService = true;
        validateServiceNode(n, push);
      } else if (hasType(n, "FAQPage")) {
        sawFaq = true;
        validateFaqNode(n, push);
      }
    }

    if (!sawClinic) push({ path: "", node: "Graph", message: "No LocalBusiness / MedicalClinic node found.", severity: "error" });
    if (!sawPhysician) push({ path: "", node: "Graph", message: "No Physician node found.", severity: "warning" });
    if (!sawService) push({ path: "", node: "Graph", message: "No MedicalProcedure / Service nodes found.", severity: "warning" });
    if (!sawFaq) push({ path: "", node: "Graph", message: "No FAQPage node found.", severity: "warning" });
  }

  const errorCount = issues.filter((i) => i.severity === "error").length;
  const warningCount = issues.filter((i) => i.severity === "warning").length;

  return {
    issues,
    errorCount,
    warningCount,
    hasError: errorCount > 0,
  };
};

export { nodeTypeLabel };
